import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { db } from '../../firebase';
import { logIn, useUserAuth } from '../../UserAuth';
import {
  joinDeck,
  loadPublicDecks,
  PUBLIC_DECK_PAGE_SIZE,
} from '../../utils/deck_service';
import { useViewportHeight } from '../../utils/viewportUtils';
import { Icon } from '../Icon';
import { MaterialHeader } from '../MaterialHeader';

export function JoinDecksPage() {
  const { user } = useUserAuth();
  const history = useHistory();
  const viewportHeight = useViewportHeight();
  const [decks, setDecks] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [joiningId, setJoiningId] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const nextDecks = await loadPublicDecks(db, user?.uid);
      setDecks(nextDecks);
      setHasMore(nextDecks.length === PUBLIC_DECK_PAGE_SIZE);
    } catch (loadError) {
      setError(loadError.message || 'Unable to load public decks.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  const loadMore = async () => {
    if (loadingMore || decks.length === 0) {
      return;
    }
    setLoadingMore(true);
    setError('');
    try {
      const nextDecks = await loadPublicDecks(db, user?.uid, {
        startAfterDeck: decks[decks.length - 1],
      });
      setDecks(current => [...current, ...nextDecks]);
      setHasMore(nextDecks.length === PUBLIC_DECK_PAGE_SIZE);
    } catch (loadError) {
      setError(loadError.message || 'Unable to load more public decks.');
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  const filteredDecks = useMemo(() => {
    const normalized = search.trim().toLowerCase();
    return normalized
      ? decks.filter(deck => String(deck.deck_name || '').toLowerCase().includes(normalized))
      : decks;
  }, [decks, search]);

  const join = async deckId => {
    if (joiningId) {
      return;
    }
    if (!user) {
      logIn();
      return;
    }
    setJoiningId(deckId);
    setError('');
    try {
      await joinDeck(db, user, deckId);
      history.replace(`/deck?id=${deckId}`);
    } catch (joinError) {
      setError(joinError.message || 'Unable to join this deck.');
      setJoiningId('');
    }
  };

  return (
    <div style={{ height: viewportHeight, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <MaterialHeader variant="close" title="Discover Decks" onClose={() => history.push('/')} />
      <main style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', padding: '16px' }}>
        <div style={{ maxWidth: '760px', width: '100%', margin: '0 auto 16px', position: 'relative' }}>
          <label htmlFor="deck-search" style={{ position: 'absolute', left: '-10000px' }}>
            Search decks by name
          </label>
          <input
            id="deck-search"
            value={search}
            onChange={event => setSearch(event.target.value)}
            placeholder="Search decks by name"
            style={{
              width: '100%',
              boxSizing: 'border-box',
              padding: '14px 48px 14px 16px',
              border: '1px solid var(--md-sys-color-outline)',
              borderRadius: '28px',
              backgroundColor: 'var(--md-sys-color-surface-container-low)',
              color: 'var(--md-sys-color-on-surface)',
              font: 'inherit',
            }}
          />
          <Icon
            name="searchDecks"
            style={{ position: 'absolute', right: '16px', top: '13px' }}
          />
        </div>
        {error && (
          <div role="alert" style={{ maxWidth: '760px', width: '100%', margin: '0 auto 12px' }}>
            {error} <button type="button" onClick={load}>Retry</button>
          </div>
        )}
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <div style={{ maxWidth: '760px', margin: '0 auto' }}>
            {loading ? (
              <div style={{ display: 'grid', placeItems: 'center', padding: '48px' }}>
                <md-circular-progress indeterminate></md-circular-progress>
              </div>
            ) : filteredDecks.map(deck => (
              <md-elevated-card
                key={deck.id}
                style={{ display: 'block', padding: '20px', marginBottom: '12px' }}
              >
                <h2 style={{ margin: '0 0 8px' }}>
                  {typeof deck.deck_name === 'string' && deck.deck_name.trim()
                    ? deck.deck_name
                    : 'Untitled Deck'}
                </h2>
                <p style={{ color: 'var(--md-sys-color-on-surface-variant)' }}>
                  {(Number.isInteger(deck.verse_count) && deck.verse_count >= 0
                    ? deck.verse_count
                    : 0).toLocaleString()} verses
                  {deck.participantCount !== null
                    ? ` · ${deck.participantCount} member${deck.participantCount === 1 ? '' : 's'}`
                    : ''}
                </p>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <md-outlined-button onClick={() => history.push(`/deck?id=${deck.id}&tab=info`)}>
                    Details
                  </md-outlined-button>
                  {deck.joined ? (
                    <md-filled-button onClick={() => history.push(`/deck?id=${deck.id}`)}>
                      Open Deck
                    </md-filled-button>
                  ) : (
                    <md-filled-button
                      {...(joiningId ? { disabled: true } : {})}
                      onClick={() => join(deck.id)}
                    >
                      <Icon name="joinDeck" slot="icon" />
                      {user ? (joiningId === deck.id ? 'Joining…' : 'Join') : 'Sign In to Join'}
                    </md-filled-button>
                  )}
                </div>
              </md-elevated-card>
            ))}
            {!loading && hasMore && (
              <div style={{ display: 'grid', placeItems: 'center', padding: '8px 0 24px' }}>
                <md-outlined-button
                  {...(loadingMore ? { disabled: true } : {})}
                  onClick={loadMore}
                >
                  {loadingMore ? 'Loading…' : 'Load More Decks'}
                </md-outlined-button>
              </div>
            )}
            {!loading && filteredDecks.length === 0 && (
              <md-elevated-card style={{ display: 'block', padding: '32px', textAlign: 'center' }}>
                <Icon name="searchOff" size="hero" />
                <h2>No decks found</h2>
              </md-elevated-card>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
